import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import useLogActivityAccess from '../hooks/useLogActivityAccess';
import './Pratica.css';

// ── Audio cache global para evitar rebuscar o mesmo áudio
const audioCache = new Map();

// ── Componente principal ────────────────────────────────────
export default function Pratica() {
  useAuth();
  useLogActivityAccess('pratica');

  // Textos
  const [textos,        setTextos]        = useState([]);
  const [textoAtual,    setTextoAtual]    = useState(null);
  // Começa aberta só em desktop; no mobile sempre fechada para não bloquear o conteúdo
  const [sidebarOpen,   setSidebarOpen]   = useState(
    typeof window !== 'undefined' && window.innerWidth > 768
  );
  const [searchTerm,    setSearchTerm]    = useState('');

  // Reprodução
  const [isPlaying,     setIsPlaying]     = useState(false);
  const [progress,      setProgress]      = useState(0);
  const [wordIndex,     setWordIndex]     = useState(-1);
  const audioRef  = useRef(null);  // Audio em reprodução
  const abortRef  = useRef(null);  // AbortController do fetch em andamento
  const wordsRef  = useRef([]);
  const timepointsRef = useRef([]); // [{ word, time }] — timestamps reais do TTS (modo sincronizado)

  // Destaque de leitura sincronizado com a voz (pode ser desativado pelo aluno)
  const [highlightEnabled, setHighlightEnabled] = useState(() => {
    try { return localStorage.getItem('pratica_highlight') !== '0'; } catch { return true; }
  });

  function toggleHighlight() {
    setHighlightEnabled(prev => {
      const next = !prev;
      try { localStorage.setItem('pratica_highlight', next ? '1' : '0'); } catch {}
      return next;
    });
  }

  // Controles TTS
  const [voices,        setVoices]        = useState([]);
  const [voiceName,     setVoiceName]     = useState('');
  const [pitch,         setPitch]         = useState(1.0);
  const [speed,         setSpeed]         = useState(1.0);

  // Tradução
  const [texto1,        setTexto1]        = useState('');
  const [texto2,        setTexto2]        = useState('');
  const [langPair,      setLangPair]      = useState('pt-en');
  const [translating,   setTranslating]   = useState(false);

  // Tamanho da fonte
  const [fontSize,      setFontSize]      = useState(17);

  // Tooltip de palavra
  const [wordTip,       setWordTip]       = useState({});

  // Notas flutuantes
  const [notesOpen,     setNotesOpen]     = useState(false);
  const [noteText,      setNoteText]      = useState('');
  const [notePos,       setNotePos]       = useState({ x: 20, y: 200 });
  const isDraggingNote  = useRef(false);
  const dragOffset      = useRef({ x: 0, y: 0 });
  const notesRef        = useRef(null);

  // Toast
  const [toast,         setToast]         = useState(null);
  const toastTimer      = useRef(null);

  function showToast(msg, type = 'success') {
    setToast({ msg, type });
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 3000);
  }

  // ── Carregar textos e vozes ao montar ─────────────────────
  useEffect(() => {
    fetch('/api/textos').then(r => r.json()).then(setTextos).catch(() => {});

    fetch('/synt/voices').then(r => r.json()).then(d => {
      const vList = d.voices || [];
      setVoices(vList);
      if (vList.length) setVoiceName(vList[0].name);
    }).catch(() => {});
  }, []);

  // ── Selecionar texto ──────────────────────────────────────
  async function selecionarTexto(id) {
    stopPlayback();
    setWordIndex(-1);
    setWordTip({});

    const data = await fetch(`/api/textos/${id}`).then(r => r.json()).catch(() => null);
    if (!data) return;

    setTextoAtual(data);
    wordsRef.current = data.conteudo.split(/\s+/);
    // No mobile, fecha sidebar ao selecionar
    if (window.innerWidth <= 768) setSidebarOpen(false);
  }

  // ── TTS: tocar texto ──────────────────────────────────────
  const playAudio = useCallback(async (text, langCode = 'en-US', { trackWords = true } = {}) => {
    stopPlayback();                    // para qualquer áudio anterior + aborta fetch anterior
    if (!text?.trim()) return;

    // Cria um controlador exclusivo para ESTE pedido
    const controller = new AbortController();
    abortRef.current = controller;

    const cacheKey = `${text}|${voiceName}|${pitch}|${speed}`;

    try {
      showToast('Processando áudio...', 'warning');
      let audioUrl;

      if (audioCache.has(cacheKey)) {
        audioUrl = audioCache.get(cacheKey);
      } else {
        const res = await fetch('/synt/text-to-speech', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          signal: controller.signal,  // ← fetch cancelável
          body: JSON.stringify({ text, languageCode: langCode, voiceName, pitch: parseFloat(pitch), speed: parseFloat(speed) }),
        });
        if (!res.ok) throw new Error('TTS falhou');
        const blob = await res.blob();
        audioUrl = URL.createObjectURL(blob);
        audioCache.set(cacheKey, audioUrl);
      }

      // Se já foi cancelado enquanto o fetch acontecia, não toca
      if (controller.signal.aborted) return;
      abortRef.current = null;

      const audio = new Audio(audioUrl);
      audio.playbackRate = parseFloat(speed);
      audioRef.current = audio;

      const words  = text.split(/\s+/);
      const nWords = words.length;

      audio.ontimeupdate = () => {
        // Ignora se este áudio já não é mais o atual
        if (audioRef.current !== audio) return;
        const pct = (audio.currentTime / audio.duration) * 100;
        setProgress(pct);
        if (trackWords) {
          const idx = Math.floor((audio.currentTime / audio.duration) * nWords);
          setWordIndex(Math.min(idx, nWords - 1));
        }
      };

      audio.onended = () => {
        if (audioRef.current !== audio) return;
        audioRef.current = null;
        setIsPlaying(false);
        setProgress(0);
        setWordIndex(-1);
        showToast('Reprodução concluída ✓');
      };

      audio.onerror = () => {
        if (audioRef.current !== audio) return;
        audioRef.current = null;
        setIsPlaying(false);
        showToast('Erro na reprodução.', 'error');
      };

      await audio.play();
      setIsPlaying(true);
    } catch (err) {
      if (err.name === 'AbortError') return;  // cancelamento esperado — silencioso
      console.error(err);
      setIsPlaying(false);
      showToast('Erro ao gerar áudio.', 'error');
    }
  }, [voiceName, pitch, speed]);

  // ── TTS sincronizado: usa marks SSML do Google TTS para saber o tempo exato
  // em que cada palavra começa a ser falada, em vez de estimar pela duração total.
  const playSyncedAudio = useCallback(async (text, langCode = 'en-US') => {
    stopPlayback();
    if (!text?.trim()) return;

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      showToast('Processando áudio...', 'warning');

      const res = await fetch('/synt/text-to-speech-sync', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal,
        body: JSON.stringify({ text, languageCode: langCode, voiceName, pitch: parseFloat(pitch) }),
      });
      if (!res.ok) throw new Error('TTS sync falhou');
      const data = await res.json();

      if (controller.signal.aborted) return;
      abortRef.current = null;

      timepointsRef.current = (data.timepoints || []).slice().sort((a, b) => a.time - b.time);

      const audio = new Audio(`data:audio/mp3;base64,${data.audioContentBase64}`);
      audio.playbackRate = parseFloat(speed);
      audioRef.current = audio;

      audio.ontimeupdate = () => {
        if (audioRef.current !== audio) return;
        const pct = (audio.currentTime / audio.duration) * 100;
        setProgress(pct);
        const tps = timepointsRef.current;
        let idx = -1;
        for (let i = 0; i < tps.length; i++) {
          if (tps[i].time <= audio.currentTime) idx = tps[i].word;
          else break;
        }
        setWordIndex(idx);
      };

      audio.onended = () => {
        if (audioRef.current !== audio) return;
        audioRef.current = null;
        setIsPlaying(false);
        setProgress(0);
        setWordIndex(-1);
        showToast('Reprodução concluída ✓');
      };

      audio.onerror = () => {
        if (audioRef.current !== audio) return;
        audioRef.current = null;
        setIsPlaying(false);
        showToast('Erro na reprodução.', 'error');
      };

      await audio.play();
      setIsPlaying(true);
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error(err);
      setIsPlaying(false);
      showToast('Erro ao gerar áudio.', 'error');
    }
  }, [voiceName, pitch, speed]);

  // ── Reproduz o texto principal, com ou sem destaque sincronizado ──
  function playMainText() {
    if (!textoAtual) return;
    if (highlightEnabled) {
      playSyncedAudio(textoAtual.conteudo);
    } else {
      playAudio(textoAtual.conteudo, 'en-US', { trackWords: false });
    }
  }

  function stopPlayback() {
    // Cancela qualquer fetch em andamento (impede que áudio antigo ainda toque)
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    // Para o áudio e libera o recurso imediatamente
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';   // força liberação do MediaElement
      audioRef.current = null;
    }
    setIsPlaying(false);
    setProgress(0);
    setWordIndex(-1);
  }

  // ── Tocar palavra individual ──────────────────────────────
  async function playWord(word) {
    if (!word.trim()) return;
    const lang = textoAtual?.idioma === 'en' ? 'en-US' : 'pt-BR';
    await playAudio(word.replace(/[^a-zA-Z0-9' ]/g, ''), lang);
  }

  // ── Tooltip (tradução da palavra) ─────────────────────────
  async function handleWordHover(word, idx) {
    if (wordTip[idx]) return;
    try {
      const res = await fetch('/traduzir/traduzir', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ texto: word.replace(/[^a-zA-Z' ]/g, ''), idiomaOrigem: 'en', idiomaDestino: 'pt' }),
      });
      const data = await res.json();
      setWordTip(prev => ({ ...prev, [idx]: data.textoTraduzido }));
    } catch {}
  }

  // ── Tradução automática ───────────────────────────────────
  const translateDebounce = useRef(null);

  useEffect(() => {
    if (!texto1.trim()) { setTexto2(''); return; }
    clearTimeout(translateDebounce.current);
    translateDebounce.current = setTimeout(async () => {
      setTranslating(true);
      const [from, to] = langPair === 'pt-en' ? ['pt', 'en'] : ['en', 'pt'];
      try {
        const res = await fetch('/traduzir/traduzir', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ texto: texto1, idiomaOrigem: from, idiomaDestino: to }),
        });
        const data = await res.json();
        setTexto2(data.textoTraduzido || '');
      } catch {
        setTexto2('Erro ao traduzir.');
      } finally {
        setTranslating(false);
      }
    }, 700);
  }, [texto1, langPair]);

  // ── Ouvir tradução ────────────────────────────────────────
  function playTranslation() {
    if (!texto2.trim()) return;
    const lang = langPair === 'pt-en' ? 'en-US' : 'pt-BR';
    playAudio(texto2, lang);
  }

  // ── Notas flutuantes — drag ───────────────────────────────
  function handleNoteDragStart(e) {
    const el = e.target;
    if (el.tagName === 'TEXTAREA' || el.tagName === 'BUTTON' || el.tagName === 'A') return;
    isDraggingNote.current = true;
    const rect = notesRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    dragOffset.current = { x: clientX - rect.left, y: clientY - rect.top };
  }

  useEffect(() => {
    function onMove(e) {
      if (!isDraggingNote.current) return;
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      setNotePos({
        x: Math.max(0, Math.min(clientX - dragOffset.current.x, window.innerWidth - 290)),
        y: Math.max(0, Math.min(clientY - dragOffset.current.y, window.innerHeight - 200)),
      });
    }
    function onUp() { isDraggingNote.current = false; }
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
  }, []);

  // ── Salvar nota ───────────────────────────────────────────
  async function saveNote() {
    if (!noteText.trim()) { showToast('Escreva algo antes de salvar.', 'warning'); return; }
    try {
      await api.post('/user/notes', { content: noteText.trim() });
      setNoteText('');
      showToast('Anotação salva!');
    } catch {
      showToast('Erro ao salvar anotação.', 'error');
    }
  }

  // ── Filtro da sidebar ─────────────────────────────────────
  const textosFiltrados = textos.filter(t =>
    t.titulo.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (t.categoria || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  // ── Renderizar texto com palavras clicáveis ───────────────
  function renderWords() {
    if (!textoAtual) return null;
    const words = textoAtual.conteudo.split(/\s+/);
    return words.map((word, i) => (
      <span
        key={i}
        className={`word${wordIndex === i ? ' highlighted' : ''}`}
        title={wordTip[i] || ''}
        onMouseEnter={() => handleWordHover(word, i)}
        onClick={() => playWord(word)}
      >
        {word}{' '}
      </span>
    ));
  }

  // ── Render ────────────────────────────────────────────────
  return (
    <div className="pratica-page">

      {/* Overlay escuro no mobile quando sidebar aberta */}
      {sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          style={{
            display: typeof window !== 'undefined' && window.innerWidth <= 768 ? 'block' : 'none',
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 99,
          }}
        />
      )}

      {/* ── Sidebar ── */}
      <aside className={`pratica-sidebar${!sidebarOpen ? ' collapsed' : ' mobile-open'}`}>
        <div className="pratica-sidebar-header">
          <i className="fas fa-book-open" />
          Textos ({textos.length})
        </div>
        <div className="pratica-sidebar-search">
          <input
            placeholder="Buscar texto..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="pratica-text-list">
          {textosFiltrados.map(t => (
            <div
              key={t.id}
              className={`pratica-text-item${textoAtual?.id === t.id ? ' active' : ''}`}
              onClick={() => selecionarTexto(t.id)}
            >
              <div className="pratica-text-item-title">{t.titulo}</div>
              <div className="pratica-text-item-meta">
                <span className={`pratica-badge ${t.nivel}`}>{t.nivel}</span>
                {t.categoria && <span className="pratica-badge cat">{t.categoria}</span>}
              </div>
            </div>
          ))}
        </div>
      </aside>

      {/* ── Main ── */}
      <div className="pratica-main">

        {/* Toolbar */}
        <div className="pratica-toolbar">
          <button className="pratica-sidebar-toggle" onClick={() => setSidebarOpen(o => !o)} title="Textos">
            <i className="fas fa-bars" />
          </button>

          {textoAtual && (
            <>
              {!isPlaying ? (
                <button className="pratica-tool-btn" onClick={playMainText}>
                  <i className="fas fa-play" /> Ouvir texto
                </button>
              ) : (
                <button className="pratica-tool-btn danger" onClick={stopPlayback}>
                  <i className="fas fa-stop" /> Parar
                </button>
              )}
            </>
          )}

          <div className="pratica-tool-group">
            <button
              className={`pratica-tool-btn${highlightEnabled ? ' active' : ''}`}
              onClick={toggleHighlight}
              title={highlightEnabled ? 'Desativar destaque de leitura' : 'Ativar destaque de leitura'}
            >
              <i className={`fas fa-${highlightEnabled ? 'highlighter' : 'eye-slash'}`} /> Destaque
            </button>
            <button className="pratica-tool-btn" onClick={() => setFontSize(f => Math.min(f + 2, 28))} title="Aumentar fonte">
              <i className="fas fa-font" /> +
            </button>
            <button className="pratica-tool-btn" onClick={() => setFontSize(f => Math.max(f - 2, 12))} title="Diminuir fonte">
              <i className="fas fa-font" /> -
            </button>
            <button className="pratica-tool-btn" onClick={() => setNotesOpen(o => !o)} title="Anotações">
              <i className="fas fa-sticky-note" />
            </button>
          </div>
        </div>

        {/* Progress */}
        <div className="pratica-progress-wrap">
          <div className="pratica-progress-bar" style={{ width: `${progress}%` }} />
        </div>

        {/* Text content */}
        <div className="pratica-content-area">
          {!textoAtual ? (
            <div className="pratica-placeholder">
              <i className="fas fa-hand-point-left" />
              <p>Selecione um texto na lista ao lado para começar.</p>
              <p style={{ fontSize: '0.85rem', color: '#bbb', marginTop: 8, fontWeight: 400 }}>
                Clique em qualquer palavra para ouvir sua pronúncia.<br />
                Passe o mouse para ver a tradução em português.
              </p>
            </div>
          ) : (
            <>
              <div className="pratica-text-title">
                {textoAtual.titulo}
                <span className={`pratica-badge ${textoAtual.nivel}`} style={{ marginLeft: 10, verticalAlign: 'middle' }}>
                  {textoAtual.nivel}
                </span>
              </div>
              <div className="pratica-text-body" style={{ fontSize }}>
                {renderWords()}
              </div>
            </>
          )}
        </div>

        {/* Controles TTS */}
        <div className="pratica-controls">
          <div className="pratica-control-group">
            <span className="pratica-control-label">Voz</span>
            <select className="pratica-select" value={voiceName} onChange={e => setVoiceName(e.target.value)}>
              {voices.map(v => (
                <option key={v.name} value={v.name}>{v.name}</option>
              ))}
            </select>
          </div>

          <div className="pratica-control-group">
            <span className="pratica-control-label">Velocidade: {parseFloat(speed).toFixed(1)}x</span>
            <input type="range" className="pratica-range" min="0.5" max="2" step="0.1" value={speed}
              onChange={e => setSpeed(e.target.value)} />
          </div>

          <div className="pratica-control-group">
            <span className="pratica-control-label">Tom: {parseFloat(pitch).toFixed(1)}</span>
            <input type="range" className="pratica-range" min="0.5" max="2" step="0.1" value={pitch}
              onChange={e => setPitch(e.target.value)} />
          </div>
        </div>

        {/* Painel de tradução */}
        <div className="pratica-translate-panel">
          <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
            <select className="pratica-select" value={langPair} onChange={e => setLangPair(e.target.value)}>
              <option value="pt-en">Português → Inglês</option>
              <option value="en-pt">Inglês → Português</option>
            </select>
            {translating && <i className="fas fa-spinner fa-spin" style={{ color: '#1e90ff' }} />}
          </div>
          <div className="pratica-translate-row">
            <div>
              <textarea
                placeholder="Digite um texto para traduzir..."
                value={texto1}
                onChange={e => setTexto1(e.target.value)}
              />
            </div>
            <div className="pratica-translate-arrow">
              <i className="fas fa-arrow-right" />
            </div>
            <div>
              <textarea placeholder="Tradução..." value={texto2} readOnly />
              {texto2 && (
                <button className="pratica-tool-btn" onClick={playTranslation}
                        style={{ marginTop: 6 }}>
                  <i className="fas fa-volume-up" /> Ouvir tradução
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Notas flutuantes ── */}
      {notesOpen && (
        <div
          ref={notesRef}
          className="pratica-notes"
          style={{ left: notePos.x, top: notePos.y }}
          onMouseDown={handleNoteDragStart}
          onTouchStart={handleNoteDragStart}
        >
          <div className="pratica-notes-header">
            <i className="fas fa-sticky-note" style={{ color: '#fbbf24' }} />
            <span className="pratica-notes-title">Minhas Anotações</span>
            <button className="pratica-notes-close" onClick={() => setNotesOpen(false)}>✕</button>
          </div>
          <div className="pratica-notes-body">
            <textarea
              placeholder="Escreva suas anotações aqui..."
              value={noteText}
              onChange={e => setNoteText(e.target.value)}
              onMouseDown={e => e.stopPropagation()}
            />
            <div className="pratica-notes-actions">
              <button className="pratica-notes-btn save" onClick={saveNote}
                      onMouseDown={e => e.stopPropagation()}>
                <i className="fas fa-save" /> Salvar
              </button>
              <Link to="/usuarios" className="pratica-notes-btn view"
                    onMouseDown={e => e.stopPropagation()}>
                <i className="fas fa-book-open" /> Ver notas
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* FAB notas */}
      {!notesOpen && (
        <button className="pratica-notes-fab" onClick={() => setNotesOpen(true)} title="Anotações">
          <i className="fas fa-sticky-note" />
        </button>
      )}

      {/* Toast */}
      {toast && <div className={`pratica-toast ${toast.type}`}>{toast.msg}</div>}
    </div>
  );
}
